import type { QueryClient } from '@tanstack/react-query'
import { getBilling } from '@/api/billing'
import { getOrder, getStatusHistory, listOrders } from '@/api/orders'
import { listUsers, getProfile, getClientDebug, userOrderStats } from '@/api/users'
import { listCompanies, getCompany } from '@/api/companies'
import { listChatMessages } from '@/api/chat'

/**
 * Тёплый старт после логина: пока пользователь ещё смотрит на дашборд,
 * фоном подтягиваем справочники и первый экран каждого раздела.
 * При первом клике в сайдбаре данные уже в кэше — не мигает «Загрузка…».
 */
export function warmUpAfterLogin(qc: QueryClient) {
  void qc.prefetchQuery({
    queryKey: ['companies', 'active'],
    queryFn: () => listCompanies({ pageSize: 200, type: 'all' }),
  })

  void qc.prefetchQuery({
    queryKey: ['users', false, ''],
    queryFn: () =>
      listUsers({
        page: 1,
        pageSize: 100,
        includeInactive: false,
        search: '',
        role: 'all',
      }),
  })

  void qc.prefetchQuery({
    queryKey: ['companies', '', false],
    queryFn: () =>
      listCompanies({
        page: 1,
        pageSize: 100,
        type: 'all',
        search: '',
        includeInactive: false,
      }),
  })
}

/**
 * Прогрев карточки пользователя по hover. Дублирует ровно те ключи,
 * что использует `UserDetailPage`. Любые изменения — синхронизировать.
 */
export function prefetchUserDetail(qc: QueryClient, userId: string) {
  void qc.prefetchQuery({
    queryKey: ['profile', userId],
    queryFn: () => getProfile(userId),
  })
  void qc.prefetchQuery({
    queryKey: ['user-orders', userId],
    queryFn: () => listOrders({ page: 1, pageSize: 100, userId }),
  })
  void qc.prefetchQuery({
    queryKey: ['user-order-stats', userId],
    queryFn: () => userOrderStats(userId),
  })
  void qc.prefetchQuery({
    queryKey: ['client-debug', userId],
    queryFn: () => getClientDebug(userId),
  })
}

/** Прогрев карточки компании по hover. */
export function prefetchCompanyDetail(qc: QueryClient, companyId: string) {
  void qc.prefetchQuery({
    queryKey: ['company', companyId],
    queryFn: () => getCompany(companyId),
  })
  void qc.prefetchQuery({
    queryKey: ['company-users', companyId],
    queryFn: () =>
      listUsers({
        page: 1,
        pageSize: 200,
        companyId,
        role: 'all',
        includeInactive: true,
      }),
  })
  void qc.prefetchQuery({
    queryKey: ['company-orders', companyId],
    queryFn: () => listOrders({ page: 1, pageSize: 100, companyId }),
  })
}

/** Прогрев карточки заказа по hover. */
export function prefetchOrderDetail(qc: QueryClient, orderId: string) {
  void qc.prefetchQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrder(orderId),
  })
  void qc.prefetchQuery({
    queryKey: ['order-history', orderId],
    queryFn: () => getStatusHistory(orderId),
  })
  void qc.prefetchQuery({
    queryKey: ['order-chat', orderId],
    queryFn: () => listChatMessages(orderId),
  })
  void qc.prefetchQuery({
    queryKey: ['billing', orderId],
    queryFn: () => getBilling(orderId),
  })
}
